import React, { useState, useEffect, useRef } from 'react'
import { X, FileText, Upload, CheckCircle, XCircle, Download, User, MapPin, Globe, Building } from 'lucide-react'
import html2pdf from 'html2pdf.js'

const TermsPopup = ({ 
  isOpen, 
  onClose, 
  profile, 
  onAgreementSubmit,
  userData 
}) => {
  const [formData, setFormData] = useState({
    contactPerson: '',
    city: '',
    country: '',
    companyAddress: '',
    companyName: '',
    registrationNumber: ''
  })
  
  const [signatureOption, setSignatureOption] = useState('upload')
  const [uploadedSignature, setUploadedSignature] = useState(null)
  const [uploadedSignatureUrl, setUploadedSignatureUrl] = useState(null)
  const [generatedSignature, setGeneratedSignature] = useState('')
  const [selectedSignatureStyle, setSelectedSignatureStyle] = useState('')
  const [agreementChecks, setAgreementChecks] = useState({
    termsAccepted: false,
    privacyAccepted: false
  })
  
  const [ipAddress, setIpAddress] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  
  const agreementRef = useRef(null)
  
  // All 8 signature styles from AcceptingAgreement component
  const signatureStyles = [
    { 
      id: 1, 
      name: 'Elegant Script', 
      template: (name) => {
        const parts = name.split(' ')
        if (parts.length > 1) {
          return `${parts[0]}\n${parts.slice(1).join(' ')}`
        }
        return name
      },
      style: 'italic text-2xl text-gray-700 dark:text-gray-300 font-poppinsRegular text-center leading-tight'
    },
    { 
      id: 2, 
      name: 'Corporate Stamp', 
      template: (name) => name,
      style: 'border-2 border-gray-500 px-4 py-3 rounded-lg bg-white dark:bg-gray-600 font-mono text-lg font-bold text-center shadow-sm'
    },
    { 
      id: 3, 
      name: 'Calligraphic', 
      template: (name) => name, // Removed symbols
      style: 'text-2xl italic text-gray-700 dark:text-gray-300 font-poppinsRegular text-center'
    },    
    { 
      id: 4, 
      name: 'Autograph Flow', 
      template: (name) => name,
      // Uses 'Great Vibes' - High formal cursive
      style: "font-['Great_Vibes'] text-4xl text-blue-900 dark:text-blue-300 text-center py-2"
    },
    { 
      id: 5, 
      name: 'Marker Script', 
      template: (name) => name,
      // Uses 'Dancing Script' - Thicker, semi-casual
      style: "font-['Dancing_Script'] text-3xl font-bold text-indigo-800 dark:text-indigo-400 text-center"
    },
    { 
      id: 6, 
      name: 'Vintage Quill', 
      template: (name) => name,
      // Uses 'Herr Von Muellerhoff' - Very elaborate, old-fashioned
      style: "font-['Herr_Von_Muellerhoff'] text-4xl text-gray-600 dark:text-gray-400 text-center leading-none"
    },
    { 
      id: 7, 
      name: 'Artistic Brush', 
      template: (name) => name,
      // Uses 'Alex Brush' - Smooth brush stroke style
      style: "font-['Alex_Brush'] text-3xl text-gray-900 dark:text-gray-100 text-center tracking-wide"
    },
    { 
      id: 8, 
      name: 'Casual Note', 
      template: (name) => name.toLowerCase(), // Lowercase looks more casual
      // Uses 'Sacramento' - Thin, mono-weight cursive
      style: "font-['Sacramento'] text-4xl text-gray-700 dark:text-gray-300 text-center"
    }
  ]

  useEffect(() => {
    if (isOpen) {
      fetchIpAddress()
      initializeFormData()
    }
    
    // Load Google Fonts for signature styles
    loadGoogleFonts()
  }, [isOpen, profile])

  // Function to load Google Fonts for signature styles
  const loadGoogleFonts = () => {
    const fonts = [
      'Great+Vibes',
      'Dancing+Script',
      'Herr+Von+Muellerhoff',
      'Alex+Brush',
      'Sacramento'
    ]
    
    fonts.forEach(font => {
      const link = document.createElement('link')
      link.href = `https://fonts.googleapis.com/css2?family=${font}&display=swap`
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    })
  }

  const initializeFormData = () => {
    setFormData({
      contactPerson: profile?.name || '',
      city: profile?.city || '',
      country: profile?.country || '',
      companyAddress: profile?.businessAddress || '',
      companyName: profile?.legalBusinessName || '',
      registrationNumber: profile?.businessRegistrationNumber || ''
    })
  }

  const fetchIpAddress = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      setIpAddress(data.ip)
    } catch (error) {
      console.error('Error fetching IP address:', error)
      setIpAddress('Unknown')
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml']
      const maxSize = 10 * 1024 * 1024

      if (!validTypes.includes(file.type)) {
        setMessage({ type: 'error', text: 'Please upload a valid image file (JPEG, PNG, SVG)' })
        return
      }

      if (file.size > maxSize) {
        setMessage({ type: 'error', text: 'File size must be less than 10MB' })
        return
      }

      setUploadedSignature(file)
      const url = URL.createObjectURL(file)
      setUploadedSignatureUrl(url)
      setMessage({ type: 'success', text: 'Signature uploaded successfully' })
    }
  }

  const handleGenerateSignature = (style) => {
    const nameToUse = formData.contactPerson || formData.companyName || 'Your Name'
    if (nameToUse && nameToUse !== 'Your Name') {
      const signature = style.template(nameToUse)
      setGeneratedSignature(signature)
      setSelectedSignatureStyle(style.name)
      setMessage({ type: 'success', text: 'Signature generated successfully' })
    } else {
      setMessage({ type: 'error', text: 'Please enter your name in the required details section first' })
    }
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setAgreementChecks(prev => ({
      ...prev,
      [name]: checked
    }))
  }

const getUpdatedAgreement = () => {
    const highlightText = (text, placeholder, userInput) => {
        if (!userInput) return text
        const regex = new RegExp(placeholder, 'g')
        return text.replace(regex, `**${userInput}**`)
    }

    let agreement = `**AXONALL GENERAL USER TERMS**

These General User Terms ("Terms") govern access to and use of the Axonall Portal, including any related User Interfaces (UI) and associated services ("Axonall Services"), by any registered third party, including but not limited to Travel Agents, Experience Providers, Destination Management Companies, Hotels, Hotel Chains, or any other authorized business or individual (each a "User").

BY REGISTERING FOR, ACCESSING, OR PARTICIPATING IN ANY PART OF THE AXONALL PLATFORM OR PROGRAMS, YOU ("USER") AGREE TO BE BOUND BY THESE TERMS AND ANY APPLICABLE AGREEMENTS OR APPENDICES. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE THE AXONALL PLATFORM.

Axonall may revise these Terms from time to time. Any material updates will be communicated via email and the Axonall Portal at least 30 days in advance. Users must affirmatively accept the amended Terms (e.g., via click-to-accept) to continue access. Non-acceptance allows termination without penalty, with existing bookings honored.

**1. INTRODUCTION**
These General User Terms (the "Terms") form part of the overarching Axonall Agreement (the "Agreement") that governs the relationship between Axonall Ltd., and any registered third party entities ("User") using the Axonall Platform.

The Agreement comprises:
(a) any supplemental terms, annexes, or variable terms (e.g., Integration Terms, Commercial Terms) issued by Axonall;
(b) any contract(s) or agreements entered into between Axonall and the User (including the Axonall Partner Agreement and/or Travel Agent Agreement, as applicable);
(c) any completed registration form, onboarding confirmation, or digital acceptance submitted via the Axonall Portal;
(d) these General User Terms; and
(e) any Axonall policies, guidelines, integration documentation, or portal materials communicated from time to time

In the event of any inconsistency, the order of precedence shall be as listed above, unless expressly stated otherwise.

**2. DEFINITIONS AND INTERPRETATION**
**2.1 Definitions:** The following definitions are used in these General Terms:

Accommodation - Means any paid lodging, including hotels, resorts, private villas, apartments, or hosted stays, offered by the User and bookable via the Axonall Platform.
Adequacy Decision - Means a decision by the UK Information Commissioner's Office (ICO), the European Commission, or another relevant data protection authority, recognizing that a third country offers an adequate level of data protection in accordance with the UK GDPR or EU GDPR. 
Affiliate - Means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. 
Agreement - Means the set of documents described under Introduction– Agreement Structure, including these General User Terms and any supplemental terms, annexes, or policies issued by Axonall.
Applicable Law - Means all applicable laws, statutes, regulations, directives, rules, codes of practice, and binding decisions or requirements of any relevant authority in any jurisdiction relevant to a party's performance of its obligations under this Agreement. 
Axonall - Means Axonall Ltd. (UK) and its affiliates, including any U.S. incorporated entity, acting jointly or severally as applicable to the User's jurisdiction. 'Affiliate' includes entities under common control, with operations in the UK or U.S. 
Axonall Brand Standards - Means the visual, experiential, and service-level guidelines issued by Axonall from time to time, which the User must adhere to when representing itself on the Axonall Platform. 
Axonall Platform - Means the suite of digital interfaces owned, operated, or licensed by Axonall, including websites, mobile applications, APIs, or third-party distribution platforms through which Services are made available to Clients. 
Axonall Portal - Means the Axonall-provided extranet and dashboards, including the Partner Portal and the Travel Agent Extranet, and any successor User Interfaces through which the User access the Axonall Services. 
Axonall Services - Means the tools, services, and functionalities made available by Axonall to support Bookings and Client engagement, including the booking engine, payment gateway, content presentation, Axonall Portal, and customer support. 
Booking - Means a confirmed reservation by a Client for Services via the Axonall Platform. 
Business Day - Means any day other than a Saturday, Sunday, or official public holiday in England. 
Client - Means any individual or entity who books or attempts to book Services through the Axonall Platform. 
Content - Means any text, images, video, audio, pricing, descriptions, or brand assets displayed or used on the Axonall Platform. 
Confidential Information - Means any non-public, commercially sensitive, or proprietary information disclosed by one party to the other, whether in writing, orally, or electronically, and marked or understood as confidential. 
Control - Means the direct or indirect ownership or control of more than 50% of the voting interest in an entity. 
Data Subject - Means an identifiable natural person whose personal data is processed under this Agreement, including Clients and representatives of the User. 
Experience or Experiences - For purposes of platform consistency, Axonall may use the term "Experience" to refer broadly to any User product, service or experience offered on the Platform, including but not limited to travel itineraries, multi-day packages, bundled services, concierge offerings, or other curated lifestyle experiences. This usage is for classification and user-experience purposes only and does not alter the underlying commercial or legal obligations associated with the User's actual offering. 
Experience, Product, Service, or Services - Means any travel, hospitality, concierge, wellness, cultural, dining, transportation, or related product or service offered by the User for Booking via the Axonall Platform. These terms may be used interchangeably within this Agreement to refer to the offering provided by the User, including curated itineraries or bundled services. 
Force Majeure Event - Means any event or circumstance beyond a party's reasonable control that prevents, hinders, or delays the performance of its obligations under this Agreement, including natural disasters, pandemics, government restrictions, or internet outages. Force Majeure shall not include business failures due to lack of funds, market changes, or internal staffing issues. 
Fraudulent Booking - Means any Booking resulting from fraudulent conduct, including use of stolen payment credentials, false identity, or malicious intent to exploit User or Platform functionality. 
Guest - Means a Client who has made and completed a Booking. 
Intellectual Property or IP - Means all registered and unregistered intellectual property rights including trademarks, copyrights, domain names, designs, trade secrets, know-how, and patents. 
Insolvency Event - Means, in relation to a party, the occurrence of liquidation, administration, bankruptcy, cessation of business, or any analogous process in any applicable jurisdiction. 
Materialized Booking - Means a confirmed Client booking made via the Axonall Platform for a Product or Service that has been fully delivered to, or attended by, the Client, and that is not a cancellation, no-show, fraudulent transaction, or refunded booking. 
Partner Portal - Means the designated extranet, dashboard, or API interface used by the intended User to manage Services, availability, rates, and communications with Axonall. 
User Registration Form - Means the online or offline registration document, onboarding form, or other agreement completed by or on behalf of the User and accepted by Axonall in connection with participation in the Axonall Programmes. 
Personal Data - Means any information relating to an identified or identifiable natural person ("Data Subject"), including but not limited to names, contact details, payment information, location data, online identifiers, or any information that, alone or in combination, enables identification. This term shall include equivalents such as "personal information" and "personally identifiable information" under applicable Technology and Data Laws. 
Personnel - Means the employees, officers, contractors, or directors of a party to this Agreement. 
Process, Processed, or Processing - Means any operation performed on Personal Data, whether or not by automated means, such as collection, recording, storage, organization, use, disclosure, transmission, deletion, or destruction. 
Processor - Means a natural or legal person or entity that processes Personal Data on behalf of a Controller, as defined under applicable data protection legislation. 
Regulator - Means any data protection authority, governmental body, or other competent public authority with legal jurisdiction to oversee compliance with Applicable Law, including Technology and Data Laws. 
Reservation - Means a confirmed Booking of a Service by a Client through the Axonall Platform. 
Taxes - Means all applicable taxes, levies, duties, charges or withholdings imposed by any local, national, or international authority, including but not limited to VAT, GST, sales tax, and tourist levies. 
Technology and Data Laws - Means any Applicable Law relating to data protection, digital services, platform operations, information security, or the processing of personal data, including but not limited to the GDPR, UK GDPR, Data Protection Act 2018, PECR, and the NIS Directive. 
Territory - Means the countries or regions in which the User's Services are offered or accepted by Axonall, subject to applicable compliance. 
Third Party Platform - Means any third-party website, application, platform, aggregator, distribution channel, or tool used for marketing, listing, or facilitating the booking of Services. 
Travel Experience - Means any travel package, Itinerary, hospitality, wellness, cultural, dining, transportation, or lifestyle-related product or service that may be offered and reserved through the Axonall Platform. 
Travel Agent Extranet - Means the Axonall-provided interface through which authorised Travel Agents may access curated content, submit client enquiries or bookings, view commission structures, and manage client interactions associated with the Axonall Platform. 
UK GDPR and GDPR - Have the meanings set out in the Data Protection Act 2018 and Regulation (EU) 2016/679, respectively. 
User - Means the business entity (e.g., Travel Agent, Experience Provider, Hotel Companies, Brand, Destination Management Company) that registers for, accesses, or uses the Axonall Platform or Axonall Services, together with its authorised employees, officers, contractors, and agents acting on its behalf via the Partner Portal, Travel Agent Extranet, APIs, or other Axonall User Interfaces. The User is responsible for all actions of such authorised persons and for ensuring their compliance with these Terms. 
User Interface (or "UIs") - Means any Axonall-provided web, mobile, API, dashboard, extranet, webhook or other interface, including the Axonall Portal, Partner Portal, and Travel Agent Extranet, through which the User accesses or uses the Axonall Services. 
Variable Terms Appendix - Means the schedule or document, if any, that sets out commercial or operational terms specific to the User, including pricing structure, commission rates, or service tiers. 

**2.2 Interpretation:**
In these General Terms:
i. References to any legislation, statute, regulation, or code shall include such law as amended, restated, extended, re-enacted, or replaced from time to time;
ii. The terms "including", "includes", "for example", or similar expressions shall be construed as illustrative and shall not limit the sense of the words preceding them;
iii. Clause, section, schedule, and paragraph headings are for convenience only and shall not affect the interpretation or construction of this Agreement.

**3. USER APPOINTMENT**
**3.1 Appointment**
Subject to the terms of this Agreement and the completion of any required technical or operational integration, Axonall grants the User non-exclusive revocable rights to access and use the Axonall Platform to offer, manage, or facilitate travel and lifestyle Products and Services for Clients.

This appointment permits the User to:
i. Access and manage Services via the Axonall Portal and related User Interfaces (UIs) as applicable,
ii. Submit, maintain, or book product and services,
iii. Participate in marketing, servicing, and operational workflows as outlined during onboarding or via technical integration.

**4. AXONALL'S RESPONSIBILITIES**
**4.1 Platform Access and Support**
Axonall shall:
i. Maintain and make available the Portal, including all relevant User Interfaces, booking tools, and Client-facing features;
ii. Operate the booking engine, payment flow, and Axonall infrastructure that supports User activity, including Bookings and Service presentation;
iii. Provide each User with secure access to the Axonall Partner Portal, Travel Agent Extranet, or relevant integration channels (e.g., API), along with user credentials and supporting documentation to manage profile, products, availability, or bookings.

These services will be delivered in all material respects in line with Axonall's integration guides, onboarding specifications, and agreed configurations.

**4.2 Changes to the Platform**
Axonall may from time to time modify the layout, features, structure, or branding of the Axonall Platform or discontinue certain functionalities, by giving reasonable notice to User (including via email or in-platform notifications). Continued use of the Platform following such notice constitutes acceptance of the updated version.

**5. USER RESPONSIBILITIES**
**5.1 Platform Use and Content Accuracy**
Each User shall:
(a) Ensure that all content under their management, including product descriptions, rates, availability, and applicable terms displayed via the Axonall Platform is accurate, up to date, and maintained in accordance with Axonall's editorial, technical, and operational guidelines.

(b) Ensure that any integration with the Axonall Platform (via Partner Portal, Travel Agent Extranet, API, or other technical method) remains functional and "always on," enabling real-time updates and availability where required.

(c) Cooperate in a timely and reasonable manner with Axonall to support the visibility, functionality, and commercial performance of their services or offerings on the Platform.

(d) Keep all login credentials, access tokens, and secure links confidential, limit access to authorized personnel only, and immediately notify Axonall of any suspected or actual unauthorized access, misuse, or breach involving their account or system integration.

**5.2 Content Submission and Review**
(a) All new experiences, products, or services intended for publication on the Axonall Platform must be submitted through the appropriate channel for review. Submission does not guarantee publication.

(b) Axonall reserves the right to:
• approve or reject any proposed listing,
• request clarification, supporting material, or verification,
• require modifications to submitted content (including pricing, visuals, or descriptions) to align with Axonall's editorial tone, legal obligations, or brand integrity.

(c) Axonall will review submissions within a commercially reasonable timeframe and notify the User of its decision. No listing may be considered published or confirmed until explicitly approved by Axonall in writing or via platform notification.

(d) Axonall may unpublish, suspend, or remove any listing if it:
• does not meet current brand, quality, or editorial standards,
• contains outdated or misleading information,
• poses legal, operational, or reputational risks,
• or breaches this Agreement or any applicable law or guideline.

**6. INTEGRATION & SERVICE SELECTIONS**
**6.1 Variable Terms**
From time to time, Axonall may offer the User access to specific integration types, enhanced services, or platform functionalities (the "Integration & Service Selections").

These options may be governed by distinct commercial or operational terms, which shall be set out in a Variable Terms Appendix or other supplemental document. Such terms shall only apply where:
(a) the relevant Integration & Service Selection has been expressly made available to the User by Axonall;
and
(b) the User has accepted or implemented that option, whether through affirmative written acceptance, onboarding, or functional activation via the Axonall Portal.

Axonall reserves the right to amend, suspend, or discontinue any Integration & Service Selection at any time upon written notice, without prejudice to existing Bookings.

**7. WARRANTIES AND DISCLAIMERS**
**7.1 General Warranties**
(a) Each party represents and warrants to the other that, at all times during the term of this Agreement:
i. it has all requisite rights, licences, consents, and authority to enter into and perform its obligations under this Agreement and to grant the rights and licences it purports to grant.

ii. it shall materially comply with all Applicable Laws, including Technology and Data Laws, in the performance of this Agreement and in its dealings with Clients and third parties.

iii. it possesses, and shall maintain throughout the term, adequately skilled personnel or representatives with the necessary expertise, knowledge, and capacity to meet its obligations in a timely, professional, and diligent manner.

iv. it shall clearly distinguish between Axonall and the User, including their respective roles, responsibilities, and platforms. The User shall not suggest or imply that it is acting on behalf of Axonall or that Axonall is responsible for the User's own platform or services. The User shall, where applicable, ensure that its terms of use, privacy policies, and public communications make this distinction clear. Axonall may require the User to update its presentation or materials to address transparency obligations or mitigate confusion.

(b) Where a Product or Service offered by the User qualifies as a "package" or "linked travel arrangement" under the Package Travel Directive (EU) 2015/2302 (or equivalent local laws), the User shall act as the "organizer" and bear sole legal responsibility for compliance, including obligations to Clients.

(c) Each party shall not create, publish, promote, or facilitate any content or conduct that is unlawful, abusive, obscene, defamatory, offensive, discriminatory, or otherwise contrary to public policy or moral standards.

**7.2 User-Specific Warranties**
(a) The User further represents and warrants that it shall not, without Axonall's express prior written consent:

i. Directly engage or negotiate with any service provider or supplier already under agreement with Axonall for the purpose of circumventing the Axonall Platform;
ii. Make Bookings on its own behalf with the intent to resell Services to third parties outside of the Axonall system;
iii. Disparage Axonall, its Affiliates, or their reputation, products, or services in public communications;
iv. Take any action that could adversely affect Axonall's market position, supplier relationships, or goodwill;
v. Exploit or partner with Third Party Platforms (e.g. metasearch, paid media, or affiliate systems) to bypass obligations under this Agreement;
vi. Use Axonall Materials (including content, listings, pricing) to offer price comparison services to Clients or the public;
vii. Launch or fund advertising (paid or organic), including search, display, or social campaigns that link to the Axonall Platform without prior written approval;
viii. Require Clients to pay in or convert payment into crypto assets or e-money without Axonall's prior written agreement.

(b) The User shall not implement any material change to how Clients interact with the Axonall Platform, or to the structure or branding of its own listings, without Axonall's prior written approval. Axonall may refuse any such change, and either party may terminate this Agreement immediately if a material change is implemented without agreement.

(c) The User must promptly notify Axonall upon becoming aware of suspected or actual fraudulent bookings, unauthorised account activity, or other security issues affecting clients or the Axonall Platform.

(d) The User operates as an independent contractor and is solely responsible for its own tax, social contributions, and regulatory obligations.

(e) The User will promptly inform Axonall if it breaches any of the warranties in this section and will cooperate in good faith to mitigate impact and implement corrective measures.

**7.3 No Implied Warranties**
(a) Except as expressly stated in this Agreement, neither party makes any representation or warranty of any kind, express or implied. Each party disclaims all implied warranties, including:
i. merchantability,
ii. fitness for a particular purpose,
iii. non-infringement,
iv. accuracy or performance of the Axonall Platform or the User's systems.

(b) The Axonall Platform and related interfaces are provided on an "as is" and "as available" basis, without any guarantee of uninterrupted access or performance.

**7.4 Insurance**
(a) The User shall, at its own cost, obtain and maintain in full force and effect throughout the term of this Agreement appropriate insurance coverage for the nature of the Services, including:
i. public liability insurance; and
ii. any other insurance required under Applicable Law (including, where relevant, professional indemnity and employer's liability insurance).

(b) Upon reasonable request, the User shall provide Axonall with evidence of such insurance, including copies of insurance certificates or confirmation from its insurer or broker.

(c) The User shall promptly notify Axonall if any required insurance is cancelled, not renewed or materially reduced in scope.

**8. INTELLECTUAL PROPERTY**
**8.1 Ownership of Axonall IP**
Axonall (or its licensors) retains all rights, title, and interest in and to the Axonall Platform, its underlying systems, curated formats, brand materials, content tools, and any associated documentation or software (the "Axonall IP").

**8.2 Ownership of User IP**
Subject to Axonall's ownership of the Axonall IP, the User (or its licensors) retains ownership of all intellectual property rights in any content, imagery, product, concepts, logos, or materials it provides to Axonall (the "User IP").

**8.3 License from Axonall**
Axonall grants the User a limited, non-exclusive, non-transferable, revocable license to access and use the Axonall Platform and associated tools solely for the purpose of submitting, managing, and fulfilling Services on the Platform.

**8.4 License from User**
The User grants Axonall (and its affiliates, subcontractors, and distribution partners) a non-exclusive, royalty-free, worldwide license to use, reproduce, adapt, distribute, and sub-license the User IP in connection with:
i. publishing and promoting the User's Services on the Axonall Platform
ii. facilitating Bookings
iii. delivering Client support
iv. and engaging in marketing and brand positioning activities, consistent with these Terms and any applicable agreement.

**8.5 Use Restrictions**
The User shall not:
i. integrate or mirror Axonall IP on other platforms,
ii. modify or create derivative works of Axonall systems or branding,
iii. use Axonall content for any competitive or commercial purpose outside of this Agreement.

**8.6 Trademark Use and Attribution**
Neither party may use the other's trademarks, logos, or brand identifiers except as expressly permitted under this Agreement or with prior written consent. All such use must comply with relevant brand guidelines.

**8.7 IP Indemnity**
Each party agrees to indemnify and hold harmless the other party against any third-party claim that its IP or content, as used under this Agreement, infringes a third party's intellectual property rights.

**8.8 Further Assurances**
Each party shall take all necessary steps and sign documents reasonably required to give full effect to the rights and licenses granted in this Clause.

**9. THIRD-PARTY CONTENT SUPPLY**
**9.1 Syndicated Materials**
(a) From time to time, the Axonall Platform may include curated content, service data, media, or experiential descriptions provided or licensed by third-party suppliers, content creators, or other affiliated providers (collectively, "Syndicated Partners"). These assets ("Syndicated Materials") may be embedded within the product and services workflow for consistency, quality, or regional accuracy.

(b) Where Syndicated Materials are used, the User agrees to honor any reasonable branding or attribution guidelines issued by Axonall or the Syndicated Partner, provided such guidelines are communicated in advance and do not conflict with the anonymized or editorial approach of the Axonall Platform.

**9.2 No Third-Party Rights**
Except as expressly stated in these Terms, no person other than the parties has any right to enforce any provision of these Terms under the Contracts (Rights of Third Parties) Act 1999.
For the avoidance of doubt, suppliers or licensors of Third-Party Materials (including any Syndicated Materials) are not intended beneficiaries of these Terms and shall have no enforcement rights.

**9.3 Pass-Through Compliance**
Where the User accesses, displays, or otherwise uses Third-Party Materials via the Axonall Platform, the User shall comply with any attribution, branding, licence, usage, or territorial restrictions notified or made available by Axonall in relation to those Third-Party Materials. The User shall not copy, export, redistribute, or reuse Third-Party Materials outside the Axonall Platform (including on the User's own channels or third-party platforms) unless expressly permitted in writing by Axonall.

**9.4 Indemnity for Misuse**
Subject to the liability exclusions and caps set out in these Terms, the User shall indemnify, defend, and hold harmless Axonall and its Affiliates from and against any Losses arising out of, or in connection with, any third-party claim alleging that the User's use of Third-Party Materials;
i. breached the applicable usage or attribution requirements notified by Axonall,
ii. was outside the scope of the licence communicated by Axonall, or
iii. was exported, copied, or reused outside the Axonall Platform without permission.

**9.5 No Warranties for Third-Party Materials.**
Third-Party Materials are provided "as is" and "as available." Axonall does not warrant, endorse, or assume responsibility for the accuracy, completeness, or continued availability of any Third-Party Materials.

**10. DATA PROTECTION AND COMPLIANCE**
**10.1 Data Protection**
Each party shall comply with all Applicable Laws relating to data protection, privacy, and information security, including:
i. the UK GDPR,
ii. the EU GDPR (where applicable),
iii. and any other Technology and Data Laws relevant to the jurisdiction in which the User operates.

The User shall ensure that:
i. it has a lawful basis for processing any Client Personal Data;
ii. it will not process, store, or use Client Personal Data for any purpose other than fulfilment of Bookings and service delivery, unless consent is obtained directly from the Client;
iii. it implements appropriate technical and organizational measures to protect Personal Data against unauthorized access, loss, or disclosure.

Axonall's data handling, security requirements, and any supplemental processing terms (if applicable) shall be made available in a separate Data Processing Agreement or incorporated via Annex.

**10.2 Security Breach Notification**
The User shall notify Axonall without undue delay and in any case within 48 hours of becoming aware of any suspected or actual data breach or incident affecting Client Personal Data or Confidential Information under this Agreement.

**10.3 Compliance**
Each party agrees to comply with all Applicable Law in the performance of this Agreement, including:
i. anti-bribery and anti-corruption laws,
ii. anti-money laundering regulations,
iii. and any specific licensing or consumer protection requirements applicable to the User's territory or services.

The User shall cooperate with Axonall in the event of any regulatory inquiry, audit, or complaint relating to services delivered under this Agreement.

**11. CONFIDENTIALITY AND PUBLICITY**
**11.1 Confidentiality Obligations**
Each party shall protect Confidential Information and use it solely for Agreement purposes. Parties shall,
i. treat as confidential all Confidential Information disclosed to it by the other party
ii. take all reasonable steps to protect such Confidential Information from unauthorized disclosure or use;
iii. use Confidential Information solely for the purposes of this Agreement; and
ensure that any third party receiving Confidential Information on its behalf is bound by confidentiality obligations no less protective than those in this Clause.

**11.2 Permitted Disclosures**
Disclosure of Confidential Information shall be permitted:
i. to a party's Affiliates, employees, contractors, advisors, or agents who have a legitimate need to know such information for the performance of the Agreement and who are subject to equivalent confidentiality obligations;
ii. by Axonall to its professional service providers, technology vendors, or affiliated contractors for the operation of the Platform or related services;
iii. if and to the extent required by law, court order, regulatory authority, or listing requirements.

**11.3 Exceptions**
This confidentiality obligation shall not apply to information that:
i. was known to the receiving party prior to disclosure without restriction;
ii. is lawfully obtained from a third party not under a duty of confidentiality;
iii. is independently developed without access to the disclosing party's Confidential Information; or
iv. becomes publicly available without breach of this Agreement.

**11.4 Publicity and Announcements**
Unless required to do so by Applicable Law, the User shall not issue or publish any press release, public announcement, media statement, or social media content referencing this Agreement, the existence of its relationship with Axonall, or the use of the Axonall Platform without Axonall's prior written consent (email shall suffice).

Axonall may, at its discretion, issue press releases or make public statements regarding its relationship with the User, including for promotional or editorial purposes, provided such statements do not disclose confidential commercial terms without prior approval.

**12. LIABILITY AND INDEMNITY**
**12.1 Platform Availability**
Each party acknowledges the inherent risks of internet-based platforms, including the potential for delays, outages, maintenance-related interruptions, and third-party infrastructure issues. Neither party shall be liable to the other for any Losses arising out of downtime, temporary unavailability, maintenance, or other operational disruptions of the Axonall Platform or the user's own system.

**12.2 Nature of Services**
The User acknowledges that Axonall acts as a curated platform facilitator, not the operator, owner, or provider of the Services themselves. Axonall is not responsible for the quality, execution, suitability, or performance of any Service provided by the User. The User assumes full responsibility and liability for its Services and their delivery to Clients.

**12.3 Indirect and Consequential Losses**
Except for liabilities that cannot legally be excluded (including death or personal injury caused by negligence, fraud or fraudulent misrepresentation, certain third-party IP claims arising from a party's own content, and breaches of data-protection obligations), neither party shall be liable for any indirect or consequential losses (including loss of profit, revenue, business, goodwill, or anticipated savings), whether in contract, tort (including negligence), breach of statutory duty, or otherwise.

**12.4 User Negligence**
Axonall shall not be liable for any failure to perform its obligations under this Agreement where such failure arises from or is attributable to any negligent act or omission by the User, its employees, agents, or subcontractors.

**12.5 Liability Cap**
Role-specific commercial caps (if any) shall be set out in the applicable Partner Agreement or Travel Agent Agreement.

**12.6 Liability That Cannot Be Limited**
The liability cap set out in these Terms does not apply to:
i. death or personal injury caused by negligence;
ii. fraud or fraudulent misrepresentation;
iii. claims by third parties arising from the User's infringement of intellectual property rights in content the User supplies or uses under these Terms;
iv. a User's breach of the data protection and security obligations described under Data Protection and Compliance or under applicable law.

Any allegation that Axonall-provided materials infringe third-party intellectual property rights will be handled under the indemnity claims procedure described in the section titled Conduct of Indemnity Claims.

**12.7 Duty to Mitigate**
Each party agrees to take all reasonable steps to mitigate any Losses it may suffer in connection with this Agreement, including losses resulting from the other party's breach.

**12.8 Conduct of Indemnity Claims**
If either party (the Indemnified Party) receives a third-party claim that is subject to indemnity by the other party (the Indemnifying Party), the following procedure shall apply:
i. The Indemnified Party shall notify the Indemnifying Party within ten (10) Business Days of becoming aware of the claim;
ii. The Indemnifying Party shall have the right to assume control of the defense and settlement of the claim, provided that any settlement likely to materially impact the Indemnified Party must be approved in advance (not to be unreasonably withheld);
iii. The Indemnified Party shall provide reasonable assistance, at the Indemnifying Party's expense;
iv. The Indemnified Party shall not admit liability or settle the claim without prior written consent of the Indemnifying Party.

**13. TERM, TERMINATION AND SUSPENSION**
**13.1 Commencement and Duration**
This Agreement shall commence on the date of last signature (or digital acceptance via the Axonall Portal, if applicable) and shall continue in full force and effect unless and until terminated in accordance with the terms set out herein.

**13.2 Voluntary Termination**
Either party may terminate this Agreement for convenience, at any time, by providing the other party with no less than thirty (30) days' written notice.

**13.3 Automatic Termination (Inactivity)**
This Agreement shall automatically terminate without further action by either party if there is no confirmed User activity resulting in completed bookings on the Axonall Portal for a continuous period of twelve (12) months.

**13.4 Suspension for Breach or Risk**
If Axonall reasonably determines that the User is:
i. in material breach of this Agreement,
ii. misrepresenting product, services, or related experiences,
iii. failing to fulfil confirmed Bookings,
iv. or presenting legal, reputational, or safety risk to Clients,
then Axonall may, at its sole discretion and without liability:
i. suspend the User's listings, profile, or access to the Platform,
ii. withhold payment for unverified or disputed Bookings,
iii. and require corrective action prior to reinstatement.

No payments will accrue during the period of suspension unless otherwise agreed in writing.

**13.5 Wind-Down Obligations**
Upon termination (whether voluntary, automatic, or for cause), both parties shall:
i. continue to honor all confirmed Bookings made prior to the effective date of termination,
ii. remain bound by all obligations necessary to ensure delivery of such products and services to Clients,
iii. and ensure a smooth wind-down that preserves Client trust and avoids disruption.

**14. CONSEQUENCES OF TERMINATION**
**14.1 Return and Removal of Axonall Materials**
Upon termination of this Agreement for any reason, the User shall, within 10 Business Days:

(a) Promptly delete, destroy, or return all Axonall Materials (including content, editorial frameworks, API credentials, access tokens, and any related documentation) in any form, and, upon request, certify such deletion, destruction, or return in writing. The User must immediately cease all use of Axonall Materials and Axonall IP.

(b) The User may retain only those copies strictly required for audit, tax, or legal compliance purposes, provided such copies:
i. are stored solely in secure backup systems;
ii. are not accessed or used for any other purpose; and
iii. continue to be treated as confidential in accordance with the confidentiality obligations in this Agreement.

(c) Promptly remove, deactivate, or disable any integration, link, API, webhook, or other interface between the User's systems and the Axonall Platform, to the extent within the User's control, and confirm in writing that such connections have been disabled.

(d) Provide reasonable assistance to support transition activities (if any), including confirming the status of any remaining Client bookings and returning or securely deleting any Client data received via the Axonall Platform.

**14.2 Accrued Rights**
Termination or expiration of this Agreement shall not affect any rights, obligations, liabilities, or claims that have already accrued prior to the termination date, including payment obligations or indemnity rights.

**14.3 Final Payment Obligations**
(a) Unless terminated by Axonall for the User's material breach, Axonall shall settle any outstanding amounts due to the User under the applicable agreement(s), provided the User has supplied complete and accurate payment details. If Axonall is unable to remit payment for more than six (6) months after termination due to incomplete or incorrect information, the User waives entitlement to such payment.

(b) Axonall may reconcile or adjust amounts for bookings that were inaccurately reported, cancelled, or failed to complete, consistent with the applicable agreement(s) between the parties.

**14.4 Clauses Surviving Termination**
The following sections survive termination or expiry: Intellectual Property; Data Protection and Compliance; Confidentiality and Publicity; Liability and Indemnity; and Consequences of Termination.

**15. ASSIGNMENT AND SUBCONTRACTING**
**15.1 General Restrictions**
Neither party may assign, transfer, novate, or subcontract any of its rights or obligations under this Agreement without the prior written consent of the other party, which shall not be unreasonably withheld, delayed, or conditioned. Notwithstanding the foregoing, Axonall may assign or transfer its rights or obligations to its U.S. affiliates for U.S. related operations upon providing written notice to the User, provided such assignment does not materially adversely affect the User's rights.

**15.2 Assignment by Axonall**
Notwithstanding general restrictions, Axonall may assign, transfer, novate, or subcontract its rights or obligations under this Agreement without the User's consent to:
i. any current or future Affiliate of Axonall; or
ii. any third party acquiring all or substantially all of the business or assets of Axonall, or to whom this Agreement relates (including as part of a merger, reorganization, or investment transaction).

The User agrees to promptly execute any documentation reasonably necessary to give effect to such transfer or novation, upon request by Axonall.

**15.3 Subcontracting for Operational Purposes**
Axonall may subcontract administrative, technical, or operational aspects of its performance under this Agreement such as payment processing, hosting, customer support, content moderation, or integration services without the User's prior consent.

**15.4 Responsibility for Subcontractors**
Each party shall remain fully liable for the acts or omissions of its subcontractors as if they were the acts or omissions of that party. Subcontracting shall not relieve a party of any obligation under this Agreement.

**16 GENERAL**
**16.1 Language**
This Agreement is written in English. Any translation is provided for convenience only. In the event of a conflict or interpretation issue, the English version shall prevail and be used in all legal proceedings.

**16.2 Costs**
Each party shall bear its own costs in connection with the negotiation, execution, and performance of this Agreement.

**16.3 Amendments**
No amendment to this Agreement shall be valid unless agreed in writing by both parties. Digital acceptance via the Axonall Portal or email confirmation shall suffice for this purpose.

**16.4 Force Majeure**
Neither party shall be liable for any failure or delay in performance under this Agreement caused by a Force Majeure Event, including natural disasters, war, cyberattacks, strikes, pandemics, or legal restrictions. The affected party must notify the other without undue delay, use commercially reasonable efforts to mitigate the impact, and take reasonable efforts to resume performance.

**16.5 Interim Relief**
Nothing in this Agreement shall prevent either party from seeking urgent legal remedies (such as an injunction) to prevent actual or threatened harm.

**16.6 Notices**
(a) Notices must be in English and may be sent to:
i. Email addresses shown in the Axonall Portal
ii. Through the Axonall Portal itself; or
iii. or by physical delivery to the address specified by the recipient.

Each party may update its contact details via the platform or by providing written notice to the other.

**16.7 Severability**
If any provision of this Agreement is held to be invalid, illegal, or unenforceable, that provision shall be modified or removed to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.

**16.8 Waiver**
No delay or omission by either party to exercise any right or remedy provided for by law or under this Agreement will operate to waive, limit or otherwise affect that right or remedy.

**16.9 No Partnership or Agency**
Nothing in this Agreement is intended to, or will be deemed to: (a) establish any partnership or joint venture between Axonall and the User, or (b) make one party the agent of the other. Neither party has the authority or power to bind, create a liability for, or enter into any commitments for or on behalf of, the other party.

**16.10 Entire Agreement**
This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations or representations. It overrides any conflicting terms in purchase orders or communications unless expressly agreed in writing.

**16.11 E-Signatures and Counterparts**
The parties agree this Agreement and any related document may be executed or accepted by electronic signature or acceptance, including click-to-accept, checkbox confirmation, typed name, scanned signature, or signatures applied via a trusted e-signature service.

**16.12 Governing Law**
This Agreement shall be governed by the laws of England and Wales, except for disputes involving U.S. users or data processed by the U.S. entity, which shall be governed by the laws of the State of Delaware (without regard to conflict of laws principles). This ensures compliance with local requirements while leveraging Delaware's business-friendly courts for U.S. matters.

**16.13 Jurisdiction**
Disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales, except for those arising under U.S. law, which shall be resolved through binding arbitration in Delaware under the American Arbitration Association (AAA) rules.

**16.14 Appendices**
This applies to all parties engaging with Axonall through the Platform ("User") under relevant terms of use or agreement. In the event of a conflict between Appendix and the remainder of the General Terms, it should be resolved in favour of this Appendix.

**APPENDIX 1: INTEGRATION & SERVICE SELECTIONS - VARIABLE TERMS**
**1. APIS AND PLATFORM INTEGRATIONS**
**1.1 API Use and Controls**
Where Axonall provides access to APIs, feeds, or digital tools to submit, update, or manage products, services, and experiences:

The User shall:
a) Comply with all reasonable technical and operational guidelines issued by Axonall regarding API use and integration.
b) Display and process all Axonall Materials received via the API accurately, consistently, and without modification unless expressly permitted.
c) Ensure data quality by submitting accurate, complete, and up-to-date information about product, service and experience (e.g. descriptions, pricing, availability).
d) Use live, real-time API calls and not cache dynamic information such as pricing, availability, or booking terms unless otherwise agreed.
e) Prevent disruption to the Axonall Platform by not creating excessive or harmful server loads, and promptly address any technical issues caused by the integration.
f) Store all authentication credentials securely within User-controlled systems and not expose them on public platforms (including mobile apps or third-party servers).

**1.2 Integration Testing and Validation**
Before going live, Axonall may require the User to:
a) Submit API integration code or Client-facing outputs for formal testing, review, and approval.
b) Provide a test environment or sandbox access for Axonall's validation team.
c) Amend or update any integration errors or deficiencies identified during testing before public launch.
d) The User must obtain Axonall's written approval before any public exposure of integrated product and experience through the API.

**1.3 Permitted Use of Axonall Materials**
The User may use Axonall Materials accessed via the API solely to display their product and services on the Platform in accordance with the Agreement.

The User may not:
a) Use Axonall Materials for any other commercial, marketing, or promotional purpose without Axonall's prior written consent.
b) Contact Clients directly for marketing unless expressly permitted.
c) Combine Axonall Materials with competitor content or price comparison services without Axonall's explicit approval.

**1.4 Price Comparison Restrictions**
Unless otherwise expressly permitted in writing by Axonall:
a) The User shall not use Axonall Materials to conduct or support price comparison services or display competing offers alongside Axonall-curated product or services.
b) Any authorized comparisons must ensure equal prominence, transparency, and fairness for Axonall listings, in compliance with Applicable Law.

**1.5 Liability for Integration Use**
The User (as Indemnifying Party) shall indemnify, defend, and hold harmless Axonall and its Affiliates (as Indemnified Party) from and against all Losses arising from:
a) Any failure by the User to comply with integration protocols,
b) Any technical or security failures that cause service interruption,
c) Any misrepresentation or misuse of Axonall Materials obtained via the API.

Any indemnity claims shall be conducted in accordance with the indemnity claims procedure set out in these Terms.

**APPENDIX 2: AXONALL COMPLIANCE REQUIREMENTS**
**1. ANTI-BRIBERY AND ANTI-CORRUPTION COMPLIANCE**
**1.1 Compliance Obligations**
Each party warrants and undertakes that, in connection with the negotiation, entry into, and performance of this Agreement, it and its Personnel, agents, subcontractors, and representatives shall:
a) Not offer, promise, give, solicit, or receive any improper payment, gift, benefit, or advantage that could be construed as an act of bribery or corruption.
b) Comply with all Applicable Laws prohibiting bribery and corruption, including without limitation the UK Bribery Act 2010, the US Foreign Corrupt Practices Act 1977, and any other similar legislation relevant to the conduct of either party.

**2. TAX COMPLIANCE**
**2.1 Tax Integrity Obligations**
Each party shall:
a) Pay all applicable Taxes due in any relevant jurisdiction,
b) Not engage in any activity that would amount to tax evasion, the facilitation of tax evasion, or fraud against any public revenue authority.

Neither party shall take any action that would cause the other party to breach any Applicable Law prohibiting tax evasion or tax fraud.

**3. TRADE SANCTIONS COMPLIANCE**
**3.1 Sanctions Warranties**
Each party represents and warrants that, for the term of this Agreement, it is not:
a) Designated on any sanctions lists administered by the United Nations, United Kingdom, European Union, or United States.
b) Owned or controlled by, or acting on behalf of, any sanctioned entity or individual.
c) Located, incorporated, or operating in a jurisdiction subject to comprehensive trade sanctions.

Neither party shall engage in any activity that would cause the other party to breach Applicable Sanctions Laws.

**4. HUMAN RIGHTS AND MODERN SLAVERY**
**4.1 Ethical Practices Warranty**
Each party warrants that it:
a) Complies with all Applicable Laws regarding human rights, anti-discrimination, and labour standards, including the UK Modern Slavery Act 2015.
b) Has not engaged, and will not engage, in any form of forced labour, servitude, slavery, or human trafficking.

**5. ANTI-MONEY LAUNDERING AND TERRORISM FINANCING**
**5.1 AML/CFT Obligations**
Each party warrants and represents that:
a) No payments, proceeds, or transactions under this Agreement shall be derived from criminal activity.
b) Neither party will knowingly be involved in any arrangement related to the financing of terrorism or money laundering.

**5.2 Bank Account and Payment Representations**
The User specifically represents that:
a) It is the lawful holder and beneficial owner of the bank account (or approved digital payment method) used for this Agreement.
b) All payments made to or from Axonall will comply with all Applicable Financial Crime Laws.
c) No payment facility used will facilitate unlawful transactions.

Use of an Alternative Payment Method (such as Stripe, Wise, or PayPal) is permitted provided it complies with all Applicable Laws.

**6. INTERNAL COMPLIANCE AND MEASURES**
**6.1 Internal Controls**
Each party shall implement and maintain internal compliance policies, procedures, training, and risk assessments reasonably designed to prevent violations of the obligations set out in this Appendix.

**7. REPORTING OBLIGATIONS**
**7.1 Disclosure and Notification**
The User agrees to:
a) Complete any compliance, due diligence, or re-screening questionnaires requested by Axonall or its appointed advisors.
b) Promptly notify Axonall in writing of any actual or suspected breach of the obligations set out in this Appendix by itself, its Personnel, or any party acting on its behalf.

**APPENDIX 3: DATA PROTECTION, PRIVACY AND SECURITY REQUIREMENTS**
**1. DATA RELATIONSHIP AND PERSONAL DATA PROCESSING**
**1.1 Roles of the Parties**
a) Each party acts as an independent Controller for the purposes of processing any Personal Data under this Agreement. Each party determines, at its sole discretion, the purposes and means of such Processing and remains responsible for its own compliance with all applicable Technology and Data Laws, including but not limited to the UK GDPR, EU GDPR, and the Data Protection Act 2018.
b) For Personal Data of U.S. residents, the parties shall comply with applicable state laws (e.g., CCPA). Axonall's U.S. entity shall act as the service provider where required, with data transfers subject to Standard Contractual Clauses or equivalent safeguards.
c) Neither party shall be deemed to act as a Processor on behalf of the other.
d) In the rare event that the parties jointly determine the purposes and means of Processing, they shall separately define their responsibilities in writing. Each party shall respond to Data Subject rights requests that it receives directly, with the other party providing reasonable support at its own cost if required.

**1.2 Personal Data Shared by Axonall**
a) Axonall may provide the User with limited Personal Data necessary to deliver Services under this Agreement (e.g. guest names, itinerary details, special requirements).

b) The User agrees to:
i. Use such Personal Data only for the purpose of delivering the specific booked Service,
ii. Not use the data for any marketing, profiling, or unrelated commercial purposes,
iii. Not disclose such data to third parties unless required by law or with Axonall's written consent.

c) The User shall not:
i. Contact or solicit Clients directly unless it is necessary for fulfilment of the Service,
ii. Use guest data to build databases, retarget, or cross-market product or services,
iii. Retain the data beyond what is necessary for lawful purposes or permitted by Axonall.

d) Axonall makes no warranty as to the accuracy or completeness of the Personal Data shared.

**1.3 Technical Integrations**
Where any data is transmitted through an Axonall-managed integration or interface, such data shall be deemed collected solely by Axonall. The User shall not intercept, store, or replicate such data independently. No joint controllership shall arise from the technical integration alone.

**1.4 Personal Data Shared by User**
If the User shares Personal Data with Axonall (e.g. for guest customization or internal processing), the following shall apply:
i. The User must obtain all necessary consents or rely on an appropriate lawful basis,
ii. The User must notify Axonall of the categories and intended use,
iii. Axonall may reject the data transfer if compliance cannot be ensured,
iv. User warrants that any such sharing complies with applicable law.

**2. DATA SECURITY AND COMPLIANCE**
**2.1 General Compliance**
Each party agrees to:
i. Maintain compliance with applicable Technology and Data Laws,
ii. Implement and regularly review privacy, data minimization, and retention policies,
iii. Avoid any Processing activity that may damage the reputation or security of the other party.

**2.2 Privacy Principles**
Each party shall ensure:
i. Lawful, fair, and transparent Processing,
ii. Purpose limitation and data minimization,
iii. Accuracy and data quality,
iv. Storage limitation and secure destruction,
v. Technical and organizational measures to prevent unauthorized access or misuse,
vi. Full traceability for demonstrating compliance.

**2.3 Cooperation**
Each party shall provide reasonable assistance and information to the other to:
i. Respond to regulatory or supervisory authority enquiries,
ii. Assist with Data Subject access or erasure requests,
iii. Facilitate data protection impact assessments (DPIAs) if required.

**3. SECURITY INCIDENTS AND BREACH MANAGEMENT**
**3.1 Prevention and Preparedness**
Each party shall implement appropriate security measures aligned with industry standards such as the UK National Cyber Security Centre (NCSC) recommendations and OWASP guidelines.

**3.2 Notification of Incidents**
If either party becomes aware of an actual or suspected security incident (including unauthorized access, data loss, breach, or system compromise), they shall:
i. Notify the other party within 48 hours,
ii. Share all relevant facts to enable containment and legal response,
iii. Consult in good faith on any communications to regulators or affected individuals,
iv. Avoid public disclosure without coordination (except where legally required).

**4. CROSS-BORDER DATA TRANSFERS**
**4.1 Transfers to Third Countries**
Personal Data may only be transferred outside the UK or EEA if the destination ensures an adequate level of protection. This may include:
i. Adequacy Decisions recognized by the UK or EU,
ii. Standard Contractual Clauses (SCCs) or International Data Transfer Agreements (IDTAs),
iii. An exception permitted under Article 49 UK/EU GDPR where necessary.

The User shall not export or transfer Axonall-collected Personal Data internationally without prior written consent.

**5. AUDIT AND INSPECTION RIGHTS**
**5.1 Right to Audit**
Axonall may, upon reasonable notice, conduct audits (or appoint a third party to do so) to verify the User's compliance with this Appendix. This includes access to security policies, systems, and personnel involved in Processing.

**5.2 Costs**
Audit costs shall be borne by Axonall unless non-compliance is identified, in which case the User shall bear the reasonable audit-related expenses.

**6. LIABILITY AND INDEMNITY**
If any regulator or third party brings a claim or imposes a fine against Axonall due to the User's non-compliance with data protection laws, the User shall:
i. Indemnify Axonall for all related Losses, costs, or penalties,
ii. Treat this as a material breach under the Agreement,
iii. Cooperate fully with Axonall's investigation and remediation actions.

**7. MISCELLANEOUS**
**7.1 Survival**
The obligations in this Appendix shall survive termination or expiry of the Agreement for as long as either party retains, or processes Personal Data collected under the Agreement.

**7.2 Data Contact**
All notices or queries relating to data protection should be addressed to legal@axonall.com (or any successor contact designated in writing by Axonall).

**DIGITAL ACCEPTANCE DETAILS:**
- Accepted by: ${formData.contactPerson || 'Authorized Signatory'}
- Company: ${formData.companyName || '[Your Company]'}
- Location: ${formData.city}, ${formData.country}
- IP Address: ${ipAddress}`

    return agreement
}

  const getCurrentSignature = () => {
    if (signatureOption === 'upload' && uploadedSignatureUrl) {
      return `\n\n[Signature: ${formData.contactPerson || 'Authorized Signatory'}]\n[Uploaded Signature Image]`
    } else if (signatureOption === 'generate' && generatedSignature) {
      const selectedStyle = signatureStyles.find(style => style.name === selectedSignatureStyle)
      
      if (selectedStyle) {
        return `\n\nSignature: ${generatedSignature}\n${formData.contactPerson || 'Authorized Signatory'}`
      }
      return `\n\nSignature: ${generatedSignature}\n${formData.contactPerson || 'Authorized Signatory'}`
    }
    return '\n\nSignature: _________________________\nName: ___________________________'
  }

  const canSubmit = () => {
    return formData.contactPerson && 
           formData.city &&
           formData.country && 
           formData.companyAddress && 
           formData.companyName &&
           (signatureOption === 'upload' ? uploadedSignature : generatedSignature) &&
           agreementChecks.termsAccepted && 
           agreementChecks.privacyAccepted
  }

  const handleSubmit = async () => {
    if (!canSubmit()) {
      setMessage({ type: 'error', text: 'Please complete all required fields and accept the terms.' })
      return
    }

    setSubmitting(true)
    try {
      const agreementData = {
        contactPerson: formData.contactPerson,
        city: formData.city,
        country: formData.country,
        companyAddress: formData.companyAddress,
        companyName: formData.companyName,
        registrationNumber: formData.registrationNumber,
        ipAddress: ipAddress,
        dateOfAcceptance: new Date().toISOString(),
        signatureType: signatureOption,
        signatureData: signatureOption === 'generate' ? {
          generatedSignature,
          signatureStyle: signatureStyles.find(s => s.name === selectedSignatureStyle) ? {
            id: signatureStyles.find(s => s.name === selectedSignatureStyle)?.id,
            name: selectedSignatureStyle,
            style: signatureStyles.find(s => s.name === selectedSignatureStyle)?.style
          } : null,
          partnerName: formData.contactPerson,
          companyName: formData.companyName
        } : null
      }

      // Call the parent's submission handler
      await onAgreementSubmit(agreementData, uploadedSignature)
      
      setMessage({ type: 'success', text: 'Agreement submitted successfully! Digital acceptance recorded.' })
      
      // Close popup after successful submission
      setTimeout(() => {
        onClose()
      }, 2000)
    } catch (error) {
      console.error('Error submitting agreement:', error)
      setMessage({ type: 'error', text: error.response?.data?.message || 'An error occurred while submitting the agreement. Please try again.' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleDownloadPDF = async () => {
    const element = agreementRef.current
    
    if (!element) {
      setMessage({ type: 'error', text: 'Could not generate PDF' })
      return
    }

    try {
      const opt = {
        margin: 10,
        filename: `Axonall-Agreement-${formData.companyName || 'Partner'}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }

      await html2pdf().set(opt).from(element).save()
      setMessage({ type: 'success', text: 'PDF downloaded successfully' })
    } catch (err) {
      console.error('PDF generation error:', err)
      setMessage({ type: 'error', text: 'Failed to generate PDF' })
    }
  }

  // Clean up object URLs on unmount
  useEffect(() => {
    return () => {
      if (uploadedSignatureUrl) {
        URL.revokeObjectURL(uploadedSignatureUrl)
      }
    }
  }, [uploadedSignatureUrl])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">AXONALL TRAVEL AGENT GENERAL TERMS</h2>
                <p className="text-slate-300">Review and accept the partnership agreement</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row h-[calc(90vh-120px)] overflow-hidden">
          {/* Left Panel - Agreement */}
          <div className="lg:w-full overflow-y-auto p-6">
            {message.text && (
              <div className={`mb-4 p-4 rounded-xl ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200 text-red-800' 
                  : 'bg-green-50 border border-green-200 text-green-800'
              }`}>
                <div className="flex items-center">
                  {message.type === 'error' ? (
                    <XCircle className="h-5 w-5 mr-2" />
                  ) : (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  )}
                  {message.text}
                </div>
              </div>
            )}

            <div ref={agreementRef} className="bg-white border border-slate-200 rounded-xl p-6">
              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap typography-body text-sm text-slate-700 leading-relaxed">
                  {getUpdatedAgreement().split('\n').map((line, index) => (
                    <div key={index} className="mb-1">
                      {line.split('**').map((part, partIndex) => 
                        partIndex % 2 === 1 ? (
                          <span key={partIndex} className="px-1 rounded font-bold">
                            {part}
                          </span>
                        ) : (
                          part
                        )
                      )}
                    </div>
                  ))}
                </div>
                

              </div>
            </div>

            {/* Download PDF Button */}
            {/* <div className="mt-4 flex justify-center">
              <button
                onClick={handleDownloadPDF}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-xl hover:from-slate-800 hover:to-slate-900 transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Download className="h-5 w-5 mr-2" />
                Download Agreement PDF
              </button>
            </div> */}
          </div>

 
        </div>
      </div>
    </div>
  )
}

export default TermsPopup